import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, MoneySend, Receipt1, Calendar } from "iconsax-react";
import { Button, FileViewer, ModalWrapper, Dropdown, DropdownItem, Prompt, PromptForm } from "@/shared/ui";
import { MoreIcon } from "@/shared/assets/icons";
import { formatDateForDisplay, formatMoneyKzt, extractErrorMessage } from "@/shared/utils";
import { useDeleteMsbCompletionActMutation } from "../../api/msb-contract";
import type { MsbCompletionAct } from "../../types/msb";
import ActStatusPipeline from "../CalculationsPage/components/ActStatusPipeline";
import InfoRow from "@/features/hr/employees/pages/WorkerDetailPage/components/InfoRow";

interface Props {
  act: MsbCompletionAct;
  locale: "ru" | "kk" | "en";
  onClose: () => void;
  onEdit?: (act: MsbCompletionAct) => void;
}

const EDITABLE_STATUSES = new Set(["draft", "pending_review", "pending_signing", "signing_pending"]);

export default function MsbActDetailModal({ act, locale, onClose, onEdit }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const canManage = EDITABLE_STATUSES.has(act.status);

  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteCompletionAct] = useDeleteMsbCompletionActMutation();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  async function handleDeleteConfirm() {
    if (deletingId !== null) return;
    setDeletingId(act.id);
    try {
      await deleteCompletionAct(act.id).unwrap();
      setConfirmDelete(false);
      setPrompt({
        title: t("calculations.actDetail.deleteSuccessTitle"),
        text: t("calculations.actDetail.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmDelete(false);
      setPrompt({
        title: t("calculations.modal.errorTitle"),
        text: extractErrorMessage(err, "calculations.actDetail.deleteError", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <ModalWrapper onClose={onClose} width="w-3xl">
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 aspect-square radius-xs surface-component-fill content-action-neutral flex items-center justify-center shrink-0">
                <DocumentText1 size={20} color="currentColor" />
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="text-display-2xs content-base-primary">
                  {t("calculations.actDetail.actNumber", { number: act.act_number })}
                </h3>
                <p className="text-body-regular-sm content-action-neutral">
                  {formatDateForDisplay(act.period_start_date)} — {formatDateForDisplay(act.period_end_date)}
                </p>
              </div>
            </div>
            {canManage && (
              <Dropdown open={menuOpen} onClose={() => setMenuOpen(false)} align="right">
                <Button
                  isIconButton
                  variant="tertiary"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="w-8! h-8! p-0! flex absolute right-10 items-center justify-center radius-xs content-base-secondary!">
                  <MoreIcon />
                </Button>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit?.(act);
                  }}>
                  {t("calculations.actDetail.edit")}
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(true);
                  }}>
                  {t("calculations.actDetail.delete")}
                </DropdownItem>
              </Dropdown>
            )}
          </div>

          <ActStatusPipeline status={act.status} isPaymentApproved={act.is_payment_approved} isPaid={act.is_paid} />

          <div className="flex flex-col pb-4 border-b surface-app-stroke">
            <InfoRow icon={MoneySend} label={t("calculations.actDetail.amount")} value={formatMoneyKzt(act.amount, locale)} />
            <InfoRow icon={Receipt1} label={t("calculations.createModal.sfNumber")} value={act.sf_number} />
            <InfoRow icon={Calendar} label={t("calculations.createModal.sfDate")} value={act.sf_date ? formatDateForDisplay(act.sf_date) : null} />
          </div>

          {act.attachment_url && (
            <div className="flex flex-col gap-2">
              <span className="text-body-regular-md content-base-secondary">{t("calculations.actDetail.attachmentLabel")}</span>
              <FileViewer existingFileUrl={act.attachment_url} customFileName={act.attachment_file_name ?? undefined} />
            </div>
          )}
        </div>
      </ModalWrapper>

      {confirmDelete && (
        <PromptForm
          title={t("calculations.actDetail.deleteConfirmTitle")}
          text={t("calculations.actDetail.deleteConfirmText")}
          variant="error"
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDeleteConfirm}
          isLoading={deletingId !== null}
          confirmText={t("calculations.actDetail.deleteConfirm")}
          cancelText={t("calculations.modal.cancel")}
          namespace="EmployeePortal"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => {
            const wasSuccess = prompt.variant === "success";
            setPrompt(null);
            if (wasSuccess) onClose();
          }}
          namespace="EmployeePortal"
        />
      )}
    </>
  );
}
